import React, { useState, useEffect, useRef } from 'react';
import { TutorialVideo, SubscriptionPlan } from '../types';
import VideoPlayerModal from './VideoPlayerModal';
import { Clapperboard, Eye, Search } from 'lucide-react';

interface VideoTutorialsSliceProps {
    editable?: boolean;
    plans: SubscriptionPlan[];
    bgConfig?: { bgUrl?: string; opacity?: number; bgColor?: string };
}

// Badge color helper (same as LandingPage features)
function getBadgeClasses(tier: string | undefined, plans: SubscriptionPlan[]): { border: string; text: string; bg: string } {
    if (!tier || tier === 'FREE') return { border: 'border-slate-500/50', text: 'text-slate-400', bg: 'bg-slate-500/10' };
    if (tier === 'XC PRO JÄHRLICH') return { border: 'border-amber-500/50', text: 'text-amber-400', bg: 'bg-amber-500/10' };
    const plan = plans.find(p => p.name === tier);
    if (!plan?.color) return { border: 'border-sky-500/50', text: 'text-sky-400', bg: 'bg-sky-500/10' };

    const colorMap: Record<string, { border: string; text: string; bg: string }> = {
        amber: { border: 'border-amber-500/50', text: 'text-amber-400', bg: 'bg-amber-500/10' },
        purple: { border: 'border-purple-500/50', text: 'text-purple-400', bg: 'bg-purple-500/10' },
        rose: { border: 'border-rose-500/50', text: 'text-rose-400', bg: 'bg-rose-500/10' },
        emerald: { border: 'border-emerald-500/50', text: 'text-emerald-400', bg: 'bg-emerald-500/10' },
        sky: { border: 'border-sky-500/50', text: 'text-sky-400', bg: 'bg-sky-500/10' },
        slate: { border: 'border-slate-500/50', text: 'text-slate-400', bg: 'bg-slate-500/10' },
    };
    return colorMap[plan.color] || colorMap.sky;
}

function getBorderColor(tier: string | undefined, plans: SubscriptionPlan[]): string {
    if (!tier || tier === 'FREE') return 'border-slate-700';
    if (tier === 'XC PRO JÄHRLICH') return 'border-amber-500';
    const plan = plans.find(p => p.name === tier);
    if (!plan?.color) return 'border-sky-500';
    const map: Record<string, string> = {
        amber: 'border-amber-500', purple: 'border-purple-500', rose: 'border-rose-500',
        emerald: 'border-emerald-500', sky: 'border-sky-500', slate: 'border-slate-500'
    };
    return map[plan.color] || 'border-sky-500';
}

function formatDuration(seconds?: number): string {
    if (!seconds) return '0:00';
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
}

const VideoTutorialsSlice: React.FC<VideoTutorialsSliceProps> = ({ editable = false, plans, bgConfig }) => {
    const [videos, setVideos] = useState<TutorialVideo[]>([]);
    const [loading, setLoading] = useState(true);
    const [selectedVideo, setSelectedVideo] = useState<TutorialVideo | null>(null);

    const [searchQuery, setSearchQuery] = useState('');
    const [filterTier, setFilterTier] = useState<string>('');
    const [categoriesOrder, setCategoriesOrder] = useState<string[]>([]);

    // Drag & Drop State (only in editable/admin mode)
    const [dragVideoId, setDragVideoId] = useState<string | null>(null);
    const [dragOverVideoId, setDragOverVideoId] = useState<string | null>(null);

    // Load videos
    const loadVideos = async () => {
        setLoading(true);
        try {
            const endpoint = editable ? '/api/tutorials/all' : '/api/tutorials';
            const res = await fetch(endpoint, {
                headers: editable ? { Authorization: `Bearer ${localStorage.getItem('xc_auth_token')}` } : {}
            });
            if (res.ok) {
                const data = await res.json();
                setVideos(data);
            }
        } catch { }
        
        try {
            const resOrder = await fetch('/api/tutorials/categories-order');
            if (resOrder.ok) setCategoriesOrder(await resOrder.json());
        } catch { }
        setLoading(false);
    };

    useEffect(() => {
        loadVideos();
    }, [editable]);

    // Group by category (with search + tier filter)
    const filteredVideos = videos.filter(v => {
        // Text search
        if (searchQuery.trim()) {
            const q = searchQuery.toLowerCase();
            if (!v.title.toLowerCase().includes(q)
                && !(v.description || '').toLowerCase().includes(q)
                && !v.category.toLowerCase().includes(q)) return false;
        }
        // Tier filter
        if (filterTier) {
            const tiers = (v.subscription_tier || '').split(',').map(t => t.trim());
            if (!tiers.includes(filterTier)) return false;
        }
        return true;
    });
    const categories = Array.from(new Set(filteredVideos.map(v => v.category))).sort((a, b) => {
        const idxA = categoriesOrder.indexOf(a);
        const idxB = categoriesOrder.indexOf(b);
        if (idxA !== -1 && idxB !== -1) return idxA - idxB;
        if (idxA !== -1) return -1;
        if (idxB !== -1) return 1;
        return a.localeCompare(b);
    });
    const groupedVideos = categories.reduce((acc, cat) => {
        acc[cat] = filteredVideos
            .filter(v => v.category === cat)
            .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
        return acc;
    }, {} as Record<string, TutorialVideo[]>);

    // Drag & Drop handlers
    const handleDragStart = (videoId: string) => {
        setDragVideoId(videoId);
    };
    const handleDragOver = (e: React.DragEvent, videoId: string) => {
        e.preventDefault();
        if (videoId !== dragVideoId) setDragOverVideoId(videoId);
    };
    const handleDrop = async (e: React.DragEvent, targetVideoId: string, category: string) => {
        e.preventDefault();
        if (!dragVideoId || dragVideoId === targetVideoId) {
            setDragVideoId(null);
            setDragOverVideoId(null);
            return;
        }

        // Reorder within the category
        const catVideos = [...(groupedVideos[category] || [])];
        const fromIdx = catVideos.findIndex(v => v.id === dragVideoId);
        const toIdx = catVideos.findIndex(v => v.id === targetVideoId);
        if (fromIdx === -1 || toIdx === -1) return;

        const [moved] = catVideos.splice(fromIdx, 1);
        catVideos.splice(toIdx, 0, moved);

        // Update sort_order for all videos in this category
        const reorderItems = catVideos.map((v, i) => ({ id: v.id, sort_order: i }));

        // Optimistic update
        setVideos(prev => {
            const newVideos = [...prev];
            for (const item of reorderItems) {
                const idx = newVideos.findIndex(v => v.id === item.id);
                if (idx !== -1) newVideos[idx] = { ...newVideos[idx], sort_order: item.sort_order };
            }
            return newVideos;
        });

        setDragVideoId(null);
        setDragOverVideoId(null);

        // Save to backend
        try {
            await fetch('/api/tutorials/reorder', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${localStorage.getItem('xc_auth_token')}`
                },
                body: JSON.stringify({ items: reorderItems })
            });
        } catch (err) {
            console.warn('Reorder save failed:', err);
        }
    };
    const handleDragEnd = () => {
        setDragVideoId(null);
        setDragOverVideoId(null);
    };

    // All valid tiers for picker
    const availableTiers = ['FREE', ...plans.filter(p => p.name !== 'FREE').map(p => p.name)].filter((v, i, a) => a.indexOf(v) === i);

    // Existing categories + free text
    const existingCategories = Array.from(new Set(videos.map(v => v.category))).filter(Boolean);

    if (!loading && videos.length === 0 && !editable) return null; // Don't show section if no videos

    return (
        <>
            <section id="tutorials" className={`py-24 relative overflow-hidden ${bgConfig?.bgColor || bgConfig?.bgUrl ? 'bg-transparent' : 'bg-slate-950'}`}>
                {/* Decorative gradient */}
                {!bgConfig?.bgColor && !bgConfig?.bgUrl && (
                    <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-purple-500/5 rounded-full blur-[120px] pointer-events-none"></div>
                )}

                <div className="container mx-auto px-6 relative z-10">
                    <div className="text-center mb-16">
                        <div className="inline-block bg-purple-500/10 border border-purple-500/30 rounded-full px-4 py-1.5 text-xs font-bold text-purple-400 uppercase tracking-widest mb-4">
                            Video-Anleitungen
                        </div>
                        <h2 className="text-3xl font-black text-white uppercase tracking-tight mb-4">
                            So funktioniert's
                        </h2>
                        <p className="text-slate-400 max-w-2xl mx-auto">
                            Schritt-für-Schritt Anleitungen für alle Funktionen. Schau dir an, wie du das Maximum aus der App herausholst.
                        </p>
                        {/* Suchfeld */}
                        <div className="mt-6 max-w-lg mx-auto relative">
                            <input
                                type="text"
                                value={searchQuery}
                                onChange={e => setSearchQuery(e.target.value)}
                                placeholder="Videos durchsuchen..."
                                className="w-full bg-slate-800/80 border border-white/10 rounded-xl px-4 py-3 pl-4 text-white text-sm focus:border-purple-500 focus:outline-none focus:ring-1 focus:ring-purple-500/50 placeholder:text-slate-500 transition-all"
                            />
                            {searchQuery && (
                                <button
                                    onClick={() => setSearchQuery('')}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-white transition-colors text-sm"
                                >✕</button>
                            )}
                        </div>
                        {/* Abo-Filter */}
                        <div className="mt-4 flex flex-wrap justify-center gap-2">
                            <button
                                onClick={() => setFilterTier('')}
                                className={`px-3 py-1.5 rounded-lg text-xs font-bold uppercase tracking-wider border transition-all ${
                                    !filterTier ? 'bg-purple-600/20 border-purple-500 text-purple-400' : 'bg-slate-800 border-white/10 text-slate-500 hover:bg-slate-700'
                                }`}
                            >Alle</button>
                            {['FREE', ...plans.filter(p => p.name !== 'FREE').map(p => p.name)].filter((v, i, a) => a.indexOf(v) === i).map(tier => {
                                const b = getBadgeClasses(tier, plans);
                                return (
                                    <button
                                        key={tier}
                                        onClick={() => setFilterTier(filterTier === tier ? '' : tier)}
                                        className={`px-3 py-1.5 rounded-lg text-xs font-bold uppercase tracking-wider border transition-all ${
                                            filterTier === tier ? `${b.bg} ${b.border} ${b.text}` : 'bg-slate-800 border-white/10 text-slate-500 hover:bg-slate-700'
                                        }`}
                                    >{tier}</button>
                                );
                            })}
                        </div>
                    </div>

                    {loading && (
                        <div className="text-center text-slate-500 py-12">
                            <div className="w-8 h-8 border-2 border-sky-500 border-t-transparent rounded-full animate-spin mx-auto mb-3"></div>
                            Lade Videos...
                        </div>
                    )}

                    {/* Videos grouped by category */}
                    {categories.map(category => (
                        <div key={category} className="mb-16 last:mb-0">
                            <h3 className="text-lg font-black text-white uppercase tracking-widest mb-6 flex items-center gap-3">
                                <span className="w-8 h-[2px] bg-gradient-to-r from-purple-500 to-transparent"></span>
                                {category}
                            </h3>

                            <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-4">
                                {groupedVideos[category].map(video => {
                                    const tiers = (video.subscription_tier || '').split(',').map(t => t.trim()).filter(Boolean);
                                    const borderColor = getBorderColor(tiers[tiers.length - 1] || undefined, plans);

                                    return (
                                        <div
                                            key={video.id}
                                            draggable={editable}
                                            onDragStart={editable ? () => handleDragStart(video.id) : undefined}
                                            onDragOver={editable ? (e) => handleDragOver(e, video.id) : undefined}
                                            onDrop={editable ? (e) => handleDrop(e, video.id, category) : undefined}
                                            onDragEnd={editable ? handleDragEnd : undefined}
                                            className={`group relative bg-slate-900/50 border ${borderColor} rounded-xl overflow-hidden transition-all hover:scale-[1.02] hover:shadow-lg cursor-pointer ${!video.is_visible ? 'opacity-50' : ''} ${dragVideoId === video.id ? 'opacity-40 scale-95' : ''} ${dragOverVideoId === video.id ? 'ring-2 ring-sky-500 ring-offset-2 ring-offset-slate-950' : ''} ${editable ? 'cursor-grab active:cursor-grabbing' : ''}`}
                                            onClick={() => !editable && setSelectedVideo(video)}
                                        >
                                            {/* Thumbnail / Play Overlay */}
                                            <div 
                                                className="relative aspect-video bg-slate-800"
                                                onClick={(e) => {
                                                    if (editable) {
                                                        e.stopPropagation();
                                                        setSelectedVideo(video);
                                                    }
                                                }}
                                            >
                                                {video.thumbnail_url ? (
                                                    <img src={video.thumbnail_url} alt={video.title} className="w-full h-full object-cover" />
                                                ) : (
                                                    <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-slate-800 to-slate-900">
                                                        <span className="text-3xl opacity-30"><Clapperboard className="w-4 h-4 inline mr-1" /></span>
                                                    </div>
                                                )}
                                                {/* Play Button Overlay */}
                                                <div className="absolute inset-0 flex items-center justify-center bg-black/30 opacity-0 group-hover:opacity-100 transition-opacity">
                                                    <div className="w-10 h-10 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center border border-white/30 shadow-lg">
                                                        <svg className="w-5 h-5 text-white ml-0.5" fill="currentColor" viewBox="0 0 24 24">
                                                            <path d="M8 5v14l11-7z"/>
                                                        </svg>
                                                    </div>
                                                </div>
                                                {/* Duration Badge */}
                                                {(video.duration_seconds || 0) > 0 && (
                                                    <div className="absolute bottom-2 right-2 bg-black/80 text-white text-xs font-mono px-2 py-0.5 rounded">
                                                        {formatDuration(video.duration_seconds)}
                                                    </div>
                                                )}
                                            </div>

                                            {/* Info */}
                                            <div className="p-4">
                                                <h4 className="text-white font-bold text-sm mb-1 line-clamp-2">{video.title}</h4>
                                                {video.description && (
                                                    <p className="text-slate-400 text-xs line-clamp-2 mb-3">{video.description}</p>
                                                )}
                                                <div className="flex flex-wrap gap-1">
                                                    {Array.from(new Set((video.subscription_tier || '').split(',').map(t => t.trim()).filter(Boolean)))
                                                        .sort((a, b) => {
                                                            const order = ['FREE', 'XC PRO MONATLICH', 'XC PRO JÄHRLICH'];
                                                            const indexA = order.indexOf(a);
                                                            const indexB = order.indexOf(b);
                                                            return (indexA === -1 ? 99 : indexA) - (indexB === -1 ? 99 : indexB);
                                                        })
                                                        .map((tier, ti) => {
                                                        const b = getBadgeClasses(tier, plans);
                                                        return (
                                                            <span key={ti} className={`text-[9px] font-black uppercase tracking-widest px-1.5 py-0.5 rounded border whitespace-nowrap ${b.border} ${b.text} ${b.bg}`}>
                                                                {tier}
                                                            </span>
                                                        );
                                                    })}
                                                </div>
                                                {editable && video.view_count !== undefined && (
                                                    <div className="text-[10px] text-slate-500 mt-1">
                                                        <Eye className="w-4 h-4 inline mr-1" /> {video.view_count} · {video.completion_rate || 0}% fertig
                                                    </div>
                                                )}
                                            </div>

                                        </div>
                                    );
                                })}
                                {/* Add New Button (Admin Editable Mode) */}
                                {editable && (
                                    <div 
                                        onClick={async () => {
                                            const title = prompt(`Neues Tutorial für "${category}" erstellen:\nBitte Titel eingeben:`);
                                            if (!title) return;
                                            try {
                                                const res = await fetch('/api/tutorials', {
                                                    method: 'POST',
                                                    headers: {
                                                        'Content-Type': 'application/json',
                                                        'Authorization': `Bearer ${localStorage.getItem('xc_auth_token') || localStorage.getItem('auth_token')}`
                                                    },
                                                    body: JSON.stringify({
                                                        title: title.trim(),
                                                        category: category,
                                                        is_visible: true,
                                                        sort_order: (groupedVideos[category] || []).length * 10
                                                    })
                                                });
                                                if (!res.ok) throw new Error(await res.text());
                                                loadVideos();
                                            } catch (e: any) {
                                                alert('Fehler beim Erstellen: ' + e.message);
                                            }
                                        }}
                                        className="group flex flex-col items-center justify-center bg-slate-900/30 border border-dashed border-slate-700 hover:border-sky-500 rounded-xl overflow-hidden transition-all hover:bg-slate-800/50 cursor-pointer min-h-[200px]"
                                    >
                                        <div className="w-12 h-12 rounded-full bg-slate-800 flex items-center justify-center group-hover:bg-sky-500 group-hover:text-white transition-colors mb-3">
                                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4"></path></svg>
                                        </div>
                                        <span className="text-sm font-semibold text-slate-400 group-hover:text-sky-300">Tutorial erstellen</span>
                                    </div>
                                )}
                            </div>
                        </div>
                    ))}


                </div>
            </section>



            {/* Video Player Modal */}
            {selectedVideo && (
                <VideoPlayerModal video={selectedVideo} onClose={() => setSelectedVideo(null)} />
            )}
        </>
    );
};

export default VideoTutorialsSlice;
